import React, { MouseEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import _ from 'lodash';
import { OrderDirection } from '../Datatable/OrderDirection';
import { invalidCargoSelector, setCorrectInvalidCargoParam, setInvalidCargoData, setInvalidCargoDisplay, setInvalidCargoOrderBy, setInvalidCargoOrderDirection, setInvalidCargoSearchText, setOpenEditInvalidCargoModal, setRefreshInvalidCargoTable, setSelectedItem } from './reducers/InvalidCargoReducer';
import moment from 'moment';
import { Box, Button, Grid, Modal } from '@mui/material';
import CargoApi from '../Cargo/api/CargoApi';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import PortSelector from '../PortSelector/PortSelector';
import PortModel from '../PortSelector/models/PortModel';
import { portSelectorSelector } from '../PortSelector/reducers/PortSelectorReducer';
import ContactSelector from '../ContactSelector/ContactSelector';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import { InvalidCargoModel } from './models/InvalidCargoModel';
import CargoTypeSelector from '../CargoTypeSelector/CargoTypeSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import { cargoTypeSelectorSelector } from '../CargoTypeSelector/reducers/CargoTypeSelectorReducer';
import CargoModel from '../Cargo/models/CargoModel';

const InvalidCargoTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let invalidCargoData = useSelector(invalidCargoSelector);
    let portSelectorData = useSelector(portSelectorSelector);
    let cargoTypeSelectorData = useSelector(cargoTypeSelectorSelector);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: "800px",
        bgcolor: 'white',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    useEffect(() => {
        CargoApi.GetInvalidCargos()
            .then((response: BaseResponse<InvalidCargoModel[]>) => {
                if (response.success) {
                    dispatch(setInvalidCargoDisplay(response.data));
                    dispatch(setInvalidCargoData(response.data));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
    }, [invalidCargoData.refreshInvalidCargoTable])

    const getCargoTypeById = (cargoTypeId: number) => {
        let selectedCargoType = _.find(cargoTypeSelectorData.cargoTypes, (cargo) => {
            return cargo.id === cargoTypeId;
        });

        if (selectedCargoType) {
            return selectedCargoType;
        }

        return null;
    }

    const columns = [
        {
            Field: "chartererCompanyName",
            Title: "Account Name",
            AllowSort: true,
            Width: "150px",
            Render: (rowData: InvalidCargoModel) => (
                <>
                    {rowData.contactActive ? rowData.chartererCompanyName : <span className='invalid-cell'>{rowData.chartererCompanyName}</span>}
                </>
            )
        } as DatatableColumnModel<InvalidCargoModel>,
        {
            Field: "dwtMin",
            Title: "Min Dwt",
            AllowSort: true,
            Render: (rowData: InvalidCargoModel) => {
                return <>{rowData.dwtMin.toLocaleString()}</>;
            }
        } as DatatableColumnModel<InvalidCargoModel>,
        {
            Field: "dwtMax",
            Title: "Max Dwt",
            AllowSort: true,
            Render: (rowData: InvalidCargoModel) => {
                return <>{rowData.dwtMax.toLocaleString()}</>;
            }
        } as DatatableColumnModel<InvalidCargoModel>,
        {
            Field: "loadPortName",
            Title: "Loadport",
            AllowSort: true,
            Render: (rowData: InvalidCargoModel) => (
                <>
                    {rowData.loadPortActive ? rowData.loadPortName : <span className='invalid-cell'>{rowData.loadPortName}</span>}
                </>
            )
        } as DatatableColumnModel<InvalidCargoModel>,
        {
            Field: "disportName",
            Title: "Disport",
            AllowSort: true,
            Render: (rowData: InvalidCargoModel) => (
                <>
                    {rowData.disportActive ? rowData.disportName : <span className='invalid-cell'>{rowData.disportName}</span>}
                </>
            )
        } as DatatableColumnModel<InvalidCargoModel>,
        {
            Field: "cargoTypeName",
            Title: "Cargo Type",
            AllowSort: true,
            Render: (rowData: InvalidCargoModel) => (
                <>
                    {rowData.cargoTypeActive ? rowData.cargoTypeName : <span className='invalid-cell'>{rowData.cargoTypeName}</span>}
                </>
            )
        } as DatatableColumnModel<InvalidCargoModel>,
        {
            Field: "",
            Title: "",
            AllowSort: false,
            Render: (rowData: InvalidCargoModel) => (
                <>
                    <Button variant='outlined'
                        onClick={() => {
                            dispatch(setCorrectInvalidCargoParam({
                                id: rowData.id,
                                charterer: rowData.contactActive ? rowData.charterer : "",
                                cargoType: rowData.cargoTypeActive ? rowData.cargoType : 0,
                                loadPort: rowData.loadPortActive ? rowData.loadPort : 0,
                                disport: rowData.disportActive ? rowData.disport : 0
                            }))
                            dispatch(setSelectedItem(rowData));
                            dispatch(setOpenEditInvalidCargoModal(true));
                        }}
                    >
                        Correct
                    </Button>
                </>
            )
        } as DatatableColumnModel<InvalidCargoModel>
    ];

    const getTableClassName = () => {
        let className = "cargo-type-management-datatable-search-result nice-scroll";
        return className;
    }

    const getPortById = (portId: number) => {
        let selectedPort = _.find(portSelectorData.ports, (port) => {
            return port.id === portId;
        });

        if (selectedPort) {
            return selectedPort;
        }

        return null;
    }

    const validate = () => {
        if (invalidCargoData.selectedItem) {
            if (!invalidCargoData.selectedItem.contactActive &&
                (
                    invalidCargoData.editItemParam.charterer === "" ||
                    invalidCargoData.editItemParam.charterer === null
                )
            ) {
                Utility.Alert.Success("Charterer is required");
                return false;
            }

            if (!invalidCargoData.selectedItem.cargoTypeActive &&
                invalidCargoData.editItemParam.cargoType === 0
            ) {
                Utility.Alert.Success("Cargo type is required");
                return false;
            }

            if (!invalidCargoData.selectedItem.loadPortActive &&
                invalidCargoData.editItemParam.loadPort === 0
            ) {
                Utility.Alert.Success("Load port is required");
                return false;
            }

            if (!invalidCargoData.selectedItem.disportActive &&
                invalidCargoData.editItemParam.disport === 0
            ) {
                Utility.Alert.Success("Disport is required");
                return false;
            }
        }

        return true;
    }

    return <div className='cargo-type-datatable-wrapper'>
        <Datatable<InvalidCargoModel>
            useDefaultSearchBar={true}
            onSearchValueChange={(value: string) => {
                dispatch(setInvalidCargoSearchText(value));
                let data = _.filter([...invalidCargoData.invalidCargoData], (item) => {
                    return item.chartererCompanyName.indexOf(value.toUpperCase()) > -1;
                })
                dispatch(setInvalidCargoDisplay(data));
            }}
            pagingType={PagingTypeEnum.None}
            data={invalidCargoData.invalidCargoDisplay}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<InvalidCargoModel>, orderDirection: OrderDirection) => {
                dispatch(setInvalidCargoOrderBy(column.Field));
                dispatch(setInvalidCargoOrderDirection(orderDirection));

                let data = [...invalidCargoData.invalidCargoDisplay];
                if (orderDirection == OrderDirection.Asc) {
                    data = _.sortBy(data, column.Field);
                } else {
                    data = _.sortBy(data, column.Field).reverse();
                }

                dispatch(setInvalidCargoDisplay(data));
            }}
            orderColumn={invalidCargoData.orderBy}
            orderDirection={invalidCargoData.orderDirection}
            stickyHeader={true}
        ></Datatable>
        <Modal
            open={invalidCargoData.openEditInvalidCargoModal}
            onClose={() => {
                dispatch(setOpenEditInvalidCargoModal(false));
            }}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={style}>
                {
                    invalidCargoData.selectedItem &&
                    <Grid container>
                        {
                            !invalidCargoData.selectedItem.contactActive &&
                            <Grid item xs={12} className="input-component">
                                <ContactSelector
                                    label='Account Name'
                                    selectedContactId={invalidCargoData.editItemParam.charterer}
                                    useFocusMode={true}
                                    onContactIdChange={(value: string) => {
                                        dispatch(setCorrectInvalidCargoParam({
                                            ...invalidCargoData.editItemParam,
                                            charterer: value
                                        }));
                                    }}
                                >
                                </ContactSelector>
                            </Grid>
                        }
                        {
                            !invalidCargoData.selectedItem.cargoTypeActive &&
                            <Grid item xs={12} className="input-component">
                                <CargoTypeSelector
                                    isGroupDisabled={true}
                                    onChange={(cargoType: CargoTypeModel | null) => {
                                        dispatch(setCorrectInvalidCargoParam({
                                            ...invalidCargoData.editItemParam,
                                            cargoType: (cargoType as CargoTypeModel).id
                                        }));
                                    }}
                                    cargoTypeValue={getCargoTypeById(invalidCargoData.editItemParam.cargoType)}
                                >
                                </CargoTypeSelector>
                            </Grid>
                        }
                        {
                            !invalidCargoData.selectedItem.loadPortActive &&
                            <Grid item xs={12} className="input-component">
                                <PortSelector
                                    onPortChange={(value: PortModel | null) => {
                                        if (value) {
                                            dispatch(setCorrectInvalidCargoParam({
                                                ...invalidCargoData.editItemParam,
                                                loadPort: (value as PortModel).id
                                            }));
                                        } else {
                                            dispatch(setCorrectInvalidCargoParam({
                                                ...invalidCargoData.editItemParam,
                                                loadPort: 0
                                            }));
                                        }
                                    }}
                                    className="cargo-input-component"
                                    portLabel='Loadport'
                                    areaLabel='Area'
                                    isAreaDisabled={true}
                                    portValue={getPortById(invalidCargoData.editItemParam.loadPort)}
                                    isAreaRelatedToPort={true}
                                >
                                </PortSelector>
                            </Grid>
                        }
                        {
                            !invalidCargoData.selectedItem.disportActive &&
                            <Grid item xs={12} className="input-component">
                                <PortSelector
                                    onPortChange={(value: PortModel | null) => {
                                        if (value) {
                                            dispatch(setCorrectInvalidCargoParam({
                                                ...invalidCargoData.editItemParam,
                                                disport: (value as PortModel).id
                                            }));
                                        } else {
                                            dispatch(setCorrectInvalidCargoParam({
                                                ...invalidCargoData.editItemParam,
                                                disport: 0
                                            }));
                                        }
                                    }}
                                    className="cargo-input-component"
                                    portLabel='Disport'
                                    areaLabel='Area'
                                    isAreaDisabled={true}
                                    portValue={getPortById(invalidCargoData.editItemParam.disport)}
                                    isAreaRelatedToPort={true}
                                >
                                </PortSelector>
                            </Grid>
                        }
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid item xs={9}>
                                </Grid>
                                <Grid item xs={3}>
                                    <Button
                                        variant='contained'
                                        fullWidth
                                        onClick={() => {
                                            if (!validate()) {
                                                return;
                                            }

                                            dispatch(showLoading(true));
                                            CargoApi.CorrectInvalid(invalidCargoData.editItemParam)
                                                .then((response: BaseResponse<CargoModel>) => {
                                                    if (response.success) {

                                                        Utility.Alert.Success("Cargo updated successful");

                                                        dispatch(showLoading(false));

                                                        dispatch(setRefreshInvalidCargoTable((new Date()).getTime()))

                                                        dispatch(setOpenEditInvalidCargoModal(false));
                                                    }
                                                })
                                        }}
                                    >Save</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Box>
        </Modal>
    </div>
};

export default InvalidCargoTable;